import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Typography,
  Box,
} from '@mui/material';
import { ChartBar } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../../context/BusinessContext';
import api from '../../../utils/api';
import DashboardCard from '../DashboardCard';

function BookingStatisticsCard() {
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [stats, setStats] = useState({ 
    total: 0, 
    pending: 0, 
    confirmed: 0, 
    completed: 0, 
    upcoming: 0,
    loading: true, 
    error: null 
  });

  const businessId = selectedBusiness?.business?._id;

  const fetchStats = useCallback(async () => {
    if (!businessId) return;

    try {
      setStats(prev => ({ ...prev, loading: true, error: null }));
      const response = await api.get('/api/unified-bookings', {
        params: {
          type: 'supplier',
          businessId,
        },
      });

      const bookings = response.data.bookings || [];
      const now = new Date();

      const statistics = {
        total: bookings.length,
        pending: bookings.filter(b => b.status === 'pending').length,
        confirmed: bookings.filter(b => b.status === 'confirmed').length,
        completed: bookings.filter(b => b.status === 'completed').length,
        upcoming: bookings.filter(b => 
          b.status === 'confirmed' && b.startTime && new Date(b.startTime) > now
        ).length,
      };

      setStats({ ...statistics, loading: false, error: null });
    } catch (error) {
      setStats({ 
        total: 0, 
        pending: 0, 
        confirmed: 0, 
        completed: 0, 
        upcoming: 0,
        loading: false, 
        error: 'Failed to load statistics' 
      });
    }
  }, [businessId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const statItems = [
    { label: 'Total', value: stats.total, color: 'primary' },
    { label: 'Pending', value: stats.pending, color: 'warning' },
    { label: 'Confirmed', value: stats.confirmed, color: 'info' },
    { label: 'Completed', value: stats.completed, color: 'success' },
    { label: 'Upcoming', value: stats.upcoming, color: 'primary' },
  ];

  return (
    <DashboardCard
      title="Booking stats"
      icon={<ChartBar size={24} weight="regular" />}
      loading={stats.loading}
      error={stats.error}
      isEmpty={!stats.loading && stats.total === 0}
      emptyMessage="No bookings yet. Figures appear here once couples start booking with you."
      emptyIcon={ChartBar}
      menuItems={[
        {
          label: 'View All Bookings',
          onClick: () => navigate('/supplier/bookings?view=overview'),
        },
      ]}
      sx={{ height: '100%', minHeight: '400px' }}
    >
      {stats.total > 0 && <Grid container spacing={2}>
        {statItems.map((item, index) => (
          <Grid item xs={6} key={index}>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            >
              <Typography variant="h4" fontWeight="bold" color={`${item.color}.main`}>
                {item.value}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {item.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>}
    </DashboardCard>
  );
}

export default BookingStatisticsCard;
